import './instrument';
import './styles.css';
import {
  getRootContainer,
  loadFonts,
  loadGoogleTagManager,
  loadKlaviyo,
  setDocumentFavicon,
  setDocumentTitle,
  setRootCSSProperty,
} from '@/api/dom';
import { loadManifest } from '@/api/public';
import { getHostname } from '@/api/window';
import { apiConfig } from '@/config';
import { Locale, Manifest, Theme } from '@/types';
import { client } from '@dosumthing/core-svc-sdk';
import i18n from 'i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { initReactI18next } from 'react-i18next';
import App from './App';

function applyTheme(manifest: Manifest) {
  const theme = manifest.theme;

  const propertyMap: Record<keyof Theme, string> = {
    primary: '--color-primary',
    primaryAlt: '--color-primary-alt',
    accent1: '--color-accent-1',
    accent1Hover: '--color-accent-1-hover',
    accent2: '--color-accent-2',
    neutralWhite: '--color-neutral-white',
    neutralLight: '--color-neutral-light',
    neutralMedium: '--color-neutral-medium',
    neutralDark: '--color-neutral-dark',
    buttonRadius: '--radius-button',
    ctaBackground: '--color-cta',
    ctaText: '--color-cta-text',
  };

  Object.entries(theme).forEach(([key, value]) => {
    const property = propertyMap[key as keyof Theme];
    if (property && value) {
      setRootCSSProperty(property, value);
    }
  });

  if (manifest.fontFamilies.display) {
    setRootCSSProperty('--font-display', manifest.fontFamilies.display);
  }

  if (manifest.fontFamilies.body) {
    setRootCSSProperty('--font-body', manifest.fontFamilies.body);
  }
}

async function initLocalization(locale: Locale) {
  await i18n
    .use(Backend)
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
      interpolation: {
        escapeValue: false, // React already escapes, so this is safe
      },
      backend: {
        loadPath: `/${locale.slug}/locales/{{lng}}.json`,
      },
      detection: {
        order: ['path', 'navigator'],
        lookupFromPathIndex: 0,
        caches: [],
      },
      supportedLngs: locale.supported,
      fallbackLng: locale.default,
      debug: import.meta.env.DEV,
      react: {
        useSuspense: true,
      },
    });
}

async function setup() {
  const hostname = getHostname();
  const manifest = await loadManifest(hostname);

  if (manifest) {
    setDocumentTitle(manifest.title);
    setDocumentFavicon(manifest.faviconUrl);
    loadFonts(manifest.fonts);
    applyTheme(manifest);

    if (manifest.googleTagManager) {
      loadGoogleTagManager({
        scriptUrl: manifest.googleTagManager.scriptUrl,
        iframeUrl: manifest.googleTagManager.iframeUrl,
      });
    }

    if (manifest.klaviyo) {
      loadKlaviyo({
        scriptUrl: manifest.klaviyo.scriptUrl,
      });
    }

    await initLocalization(manifest.locale);
  }

  client.setConfig({
    baseUrl: apiConfig.url,
    throwOnError: false,
  });

  return manifest;
}

function renderApp(manifest: Manifest | undefined) {
  const container = getRootContainer();

  if (!container) return;

  ReactDOM.createRoot(container).render(
    <React.Suspense fallback={<div></div>}>
      <App manifest={manifest} />
    </React.Suspense>,
  );
}

setup()
  .then((manifest) => renderApp(manifest))
  .catch((error) => {
    console.error('Failed to initialize application:', error);
  });
